import {
  formatDate,
  formatExactDate,
  formatSeconds,
  getReportDate,
  getReportTime
} from '../../../../../Helpers';
import { Field, FieldValueType } from '../../../../../Models';
import { UserGroup, UserTitle } from 'ui-smartforce-settings';
import {
  CadAddressValueType,
  CadOffenseValueType,
  CadPersonValueType,
  CadPremiseValueType,
  CadSubjectValueType
} from '../../../../../Models/CadReportValue';

interface FieldComponentDictType {
  [key: string]: string;
}

export const FieldComponentDict: FieldComponentDictType = {
  autocomplete: 'string',
  autocompletechip: 'array',
  checkbox: 'array',
  date: 'date',
  datetime: 'datetime',
  duration: 'duration',
  location: 'location',
  multiselect: 'array',
  number: 'number',
  radio: 'string',
  textfield: 'string',
  textarea: 'string',
  time: 'time',
  switch: 'boolean',
  itemlist: 'itemlist',
  citizen_checklist: 'citizen_checklist',
  groupsfield: 'grouplist',
  titlefield: 'title',
  peoplepicker: 'string'
};

export const getFieldFormattedValue = (
  fieldValue: FieldValueType,
  type: string,
  timezone: string
): string => {
  switch (type) {
    case 'date':
      return getReportDate(fieldValue as string, timezone);
    case 'time': {
      return getReportTime(fieldValue as string, timezone);
    }
    case 'datetime':
      return formatDate(fieldValue as string, 'L - HH:mm', timezone);
    case 'exactdate':
      return formatExactDate(fieldValue as string, 'MM/DD/YYYY');
    case 'seconds':
      return formatSeconds(fieldValue as number);
    case 'boolean':
      return fieldValue ? 'Yes' : 'No';
    case 'array':
      return (fieldValue as FieldValueType[])
        .map((value: FieldValueType) => `${value}`)
        .join(', ');
    case 'grouplist':
      return [...(fieldValue as UserGroup[])]
        .sort((a: UserGroup, b: UserGroup) => a.name.localeCompare(b.name))
        .map((value: UserGroup) => `${value.name}`)
        .join(', ');
    case 'citizen_checklist':
      return (fieldValue as number[])
        .map((value: number) => `Citizen ${value + 1}`)
        .join(', ');
    case 'title':
      return (fieldValue as UserTitle).name;
    default:
      return `${fieldValue}`;
  }
};

export const getFieldType = (config: Field): string => {
  return FieldComponentDict[config.component];
};

/**
 * CAD specific helpers
 */
const getCADPersonNameValue = (
  person: CadPersonValueType | CadSubjectValueType,
  badgeId?: string
): string => {
  return `${[person.last_name, [person.first_name, person.middle_name].join(' ')].join(', ')} ${badgeId ? '#' + badgeId : ''}`;
};

export const getCADPersonnelFullName = (
  badgeId: string,
  personnel: CadPersonValueType[]
): string => {
  const foundPerson = personnel.find((p) => p.badge_id === badgeId);
  return foundPerson
    ? getCADPersonNameValue(foundPerson, badgeId)
    : badgeId
      ? `${badgeId}`
      : '';
};

export const getCADSubjectFullName = (
  id: string,
  subjects: CadSubjectValueType[]
): string => {
  const subject = subjects.find((s) => s.id === id);
  return subject ? getCADPersonNameValue(subject) : '';
};

export const getCADOffenseLabel = (
  id: string,
  offenses: CadOffenseValueType[]
): string => {
  const offense = offenses.find((o) => o.id === id);
  return offense ? `${offense.code} - ${offense.code_description}` : '';
};

export const getCADPremiseName = (
  premiseId: string,
  premises: CadPremiseValueType[]
): string => {
  const premise = premises.find((premise) => premise.id === premiseId);
  return premise && premise.name ? premise.name : '';
};

export const getCADAddressFull = (
  addressId: string,
  addresses: CadAddressValueType[]
): string => {
  const address = addresses.find((address) => address.id === addressId);
  return address ? address.full : '';
};
